﻿(() => {
  const WRAPPER_SELECTOR = '[data-stamped-split-sections]';
  const APPS_SECTION_SELECTOR = '.shopify-section--apps, .shopify-section--legacy-apps';

  const findWidget = (wrapper) =>
    wrapper.querySelector('#stamped-main-widget, [id^="stamped-main-widget"], .stamped-main-widget');

  const findReviewsPanel = (widget) =>
    widget.querySelector('#stamped-reviews, #stamped-reviews-tab, .stamped-reviews');

  const findQuestionsPanel = (widget) => {
    const selectors = [
      '#stamped-questions-tab',
      '#stamped-questions',
      '.stamped-questions',
    ];

    for (const selector of selectors) {
      const panel = widget.querySelector(selector);
      if (panel && !panel.closest('.stamped-questions-placeholder')) {
        return panel;
      }
    }

    const tabContainer = widget.querySelector('.stamped-tab-container, ul.stamped-tabs');
    if (!tabContainer) {
      return null;
    }

    let sibling = tabContainer.nextElementSibling;
    while (sibling) {
      if (
        sibling.matches('#stamped-questions-tab, #stamped-questions, .stamped-questions')
      ) {
        return sibling;
      }

      sibling = sibling.nextElementSibling;
    }

    return null;
  };

  const createSectionHeader = (heading) => {
    const header = document.createElement('div');
    header.className = 'section-header stamped-split-section__header justify-self-start text-start';
    header.innerHTML = `<div class="prose"><h2 class="h2">${heading}</h2></div>`;
    return header;
  };

  const getSectionScrollTop = (section) => {
    const style = window.getComputedStyle(section);
    const scrollMarginTop = parseFloat(style.scrollMarginTop) || 0;
    return Math.max(0, section.getBoundingClientRect().top + window.scrollY - scrollMarginTop);
  };

  const scrollToSplitSection = (section, behavior = 'auto') => {
    if (!section) {
      return;
    }

    window.scrollTo({ top: getSectionScrollTop(section), behavior });
  };

  const isQuestionsPagination = (pagination) =>
    Boolean(
      pagination.id === 'stamped-pagination-question' ||
      pagination.matches('[id*="pagination-question"]') ||
      pagination.closest('#stamped-questions-section, .stamped-split-section--questions, [data-stamped-questions-target]'),
    );

  const getPaginationTargetSection = (wrapper, pagination) => {
    if (isQuestionsPagination(pagination)) {
      return wrapper.querySelector('#stamped-questions-section, .stamped-split-section--questions');
    }

    return wrapper.querySelector('#stamped-reviews-section, .stamped-split-section--reviews');
  };

  const installWidgetScrollGuard = (wrapper) => {
    const widget = findWidget(wrapper);
    if (!widget || widget.dataset.stampedScrollGuard === 'true') {
      return;
    }

    widget.dataset.stampedScrollGuard = 'true';
    const nativeScrollIntoView = widget.scrollIntoView.bind(widget);

    widget.scrollIntoView = function (...args) {
      if (wrapper.dataset.stampedQuestionsPaginationActive === 'true') {
        const questionsSection = wrapper.querySelector('#stamped-questions-section, .stamped-split-section--questions');
        scrollToSplitSection(questionsSection, 'auto');
        return;
      }

      return nativeScrollIntoView(...args);
    };
  };

  const setQuestionsPaginationActive = (wrapper, active) => {
    wrapper.dataset.stampedQuestionsPaginationActive = active ? 'true' : 'false';
  };

  const activateQuestionsPaginationScroll = (wrapper, targetSection) => {
    installWidgetScrollGuard(wrapper);
    setQuestionsPaginationActive(wrapper, true);
    scrollToSplitSection(targetSection, 'auto');

    const lockScrollToQuestions = () => {
      if (wrapper.dataset.stampedQuestionsPaginationActive !== 'true') {
        return;
      }

      const targetTop = getSectionScrollTop(targetSection);
      if (window.scrollY + 2 < targetTop) {
        window.scrollTo({ top: targetTop, behavior: 'auto' });
      }
    };

    if (wrapper._stampedQuestionsScrollLock) {
      window.removeEventListener('scroll', wrapper._stampedQuestionsScrollLock);
    }

    wrapper._stampedQuestionsScrollLock = lockScrollToQuestions;
    window.addEventListener('scroll', lockScrollToQuestions, { passive: true });

    if (wrapper._stampedQuestionsScrollGuardTimer) {
      window.clearTimeout(wrapper._stampedQuestionsScrollGuardTimer);
    }

    wrapper._stampedQuestionsScrollGuardTimer = window.setTimeout(() => {
      setQuestionsPaginationActive(wrapper, false);

      if (wrapper._stampedQuestionsScrollLock === lockScrollToQuestions) {
        window.removeEventListener('scroll', lockScrollToQuestions);
        wrapper._stampedQuestionsScrollLock = null;
      }

      wrapper._stampedQuestionsScrollGuardTimer = null;
    }, 3000);
  };

  const scheduleSectionScroll = (wrapper, pagination, { skipInitial = false } = {}) => {
    const targetSection = getPaginationTargetSection(wrapper, pagination);
    if (!targetSection) {
      return;
    }

    if (wrapper._stampedScrollObserver) {
      wrapper._stampedScrollObserver.disconnect();
      wrapper._stampedScrollObserver = null;
    }

    if (Array.isArray(wrapper._stampedScrollTimeouts)) {
      wrapper._stampedScrollTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
    }
    wrapper._stampedScrollTimeouts = [];

    const contentRoot =
      targetSection.querySelector('[data-stamped-questions-target]') ||
      targetSection.querySelector('.stamped-split-section__content') ||
      targetSection;

    const observer = new MutationObserver(() => {
      scrollToSplitSection(targetSection, 'auto');
    });

    observer.observe(contentRoot, { childList: true, subtree: true });
    wrapper._stampedScrollObserver = observer;

    if (!skipInitial) {
      wrapper._stampedScrollTimeouts.push(
        window.setTimeout(() => {
          scrollToSplitSection(targetSection, 'auto');
        }, 120),
      );
    }

    [600, 1200].forEach((delay) => {
      wrapper._stampedScrollTimeouts.push(
        window.setTimeout(() => {
          scrollToSplitSection(targetSection, 'auto');
        }, delay),
      );
    });

    wrapper._stampedScrollTimeouts.push(
      window.setTimeout(() => {
        observer.disconnect();
        if (wrapper._stampedScrollObserver === observer) {
          wrapper._stampedScrollObserver = null;
        }
      }, 2500),
    );
  };

  const setupPaginationScroll = (wrapper) => {
    if (wrapper.dataset.stampedPaginationScroll === 'true') {
      return;
    }

    wrapper.dataset.stampedPaginationScroll = 'true';
    installWidgetScrollGuard(wrapper);

    wrapper.addEventListener(
      'click',
      (event) => {
        const link = event.target.closest('a');
        if (!link || !wrapper.contains(link)) {
          return;
        }

        const pagination = link.closest(
          '.stamped-pagination, #stamped-pagination-question, [id*="stamped-pagination"]',
        );
        if (!pagination) {
          return;
        }

        const targetSection = getPaginationTargetSection(wrapper, pagination);
        if (!targetSection) {
          return;
        }

        if (isQuestionsPagination(pagination)) {
          const href = link.getAttribute('href') || '';
          if (href.startsWith('#') || href.includes('stamped-main-widget')) {
            event.preventDefault();
          }

          activateQuestionsPaginationScroll(wrapper, targetSection);
          scheduleSectionScroll(wrapper, pagination, { skipInitial: true });
          return;
        }

        scheduleSectionScroll(wrapper, pagination);
      },
      true,
    );
  };

  const bootstrapWrapper = (section) => {
    const widget = section.querySelector('#stamped-main-widget, [id^="stamped-main-widget"]');
    if (!widget) {
      return null;
    }

    const appBlock = widget.closest('.shopify-app-block, .shopify-block');
    if (!appBlock) {
      return null;
    }

    const configHost = section.querySelector('[data-stamped-reviews-heading]') || section;
    const reviewsHeading = configHost.dataset.stampedReviewsHeading || 'レビュー';
    const questionsHeading = configHost.dataset.stampedQuestionsHeading || '質問';

    const wrapper = document.createElement('div');
    wrapper.className = 'stamped-split-sections';
    wrapper.setAttribute('data-stamped-split-sections', '');

    const reviewsSection = document.createElement('section');
    reviewsSection.className = 'stamped-split-section stamped-split-section--reviews';
    reviewsSection.id = 'stamped-reviews-section';
    reviewsSection.setAttribute('aria-label', reviewsHeading);
    reviewsSection.appendChild(createSectionHeader(reviewsHeading));

    const reviewsContent = document.createElement('div');
    reviewsContent.className = 'stamped-split-section__content';
    reviewsContent.appendChild(appBlock);
    reviewsSection.appendChild(reviewsContent);

    const questionsSection = document.createElement('section');
    questionsSection.className = 'stamped-split-section stamped-split-section--questions';
    questionsSection.id = 'stamped-questions-section';
    questionsSection.setAttribute('aria-label', questionsHeading);
    questionsSection.appendChild(createSectionHeader(questionsHeading));

    const questionsTarget = document.createElement('div');
    questionsTarget.className = 'stamped-split-section__content';
    questionsTarget.setAttribute('data-stamped-questions-target', '');
    questionsSection.appendChild(questionsTarget);

    wrapper.appendChild(reviewsSection);
    wrapper.appendChild(questionsSection);

    const container = section.querySelector('.legacy-container') || section;
    container.appendChild(wrapper);

    return wrapper;
  };

  const ensureWrappers = () => {
    document.querySelectorAll(APPS_SECTION_SELECTOR).forEach((section) => {
      if (!section.querySelector(WRAPPER_SELECTOR)) {
        bootstrapWrapper(section);
      }
    });
  };

  const hideTabs = (wrapper) => {
    wrapper.querySelectorAll('.stamped-tab-container, ul.stamped-tabs').forEach((element) => {
      element.setAttribute('hidden', '');
      element.style.display = 'none';
    });
  };

  const REVIEW_UI_SELECTORS = [
    '#stamped-reviews',
    '#stamped-reviews-tab',
    '.stamped-reviews',
    '.stamped-reviews-filter',
    '.stamped-sort-select2-wrapper',
    '#stamped-sort-select2',
  ];

  const QUESTION_UI_SELECTORS = [
    '#stamped-questions-tab',
    '#stamped-questions',
    '.stamped-questions',
    '.stamped-questions-filter',
    '.stamped-questions-search-text',
    '.stamped-filter-select',
    '.stamped-filter-selects',
    '.stamped-sort-select2-wrapper',
    '#stamped-sort-select-questions',
    '#stamped-pagination-question',
  ];

  const hideReviewsSearch = (wrapper) => {
    wrapper.querySelectorAll('.stamped-split-section--reviews .stamped-reviews-search-text').forEach((element) => {
      element.setAttribute('hidden', '');
      element.style.display = 'none';
    });
  };

  const hideNativeSortSelects = (wrapper) => {
    wrapper
      .querySelectorAll('select#stamped-sort-select, select#stamped-sort-select2, .stamped-filter-select select')
      .forEach((element) => {
      element.setAttribute('aria-hidden', 'true');
      element.tabIndex = -1;
      element.style.position = 'absolute';
      element.style.width = '1px';
      element.style.height = '1px';
      element.style.padding = '0';
      element.style.margin = '-1px';
      element.style.overflow = 'hidden';
      element.style.clip = 'rect(0, 0, 0, 0)';
      element.style.whiteSpace = 'nowrap';
      element.style.border = '0';
      element.style.opacity = '0';
      element.style.pointerEvents = 'none';
    });
  };

  const relocateQuestionsControls = (wrapper, questionsTarget) => {
    const reviewsRoot = wrapper.querySelector('.stamped-split-section--reviews');
    if (!reviewsRoot || !questionsTarget) {
      return null;
    }

    const questionsPanelRoot =
      questionsTarget.querySelector('#stamped-questions-tab, #stamped-questions, .stamped-questions') ||
      questionsTarget;

    reviewsRoot
      .querySelectorAll(
        '.stamped-questions-search-text, .stamped-questions-filter, .stamped-questions-toolbar, #stamped-pagination-question',
      )
      .forEach((element) => {
        if (!questionsPanelRoot.contains(element)) {
          questionsPanelRoot.insertBefore(element, questionsPanelRoot.firstChild);
        }
      });

    return questionsPanelRoot;
  };

  const buildQuestionsToolbar = (questionsPanel) => {
    if (!questionsPanel) {
      return;
    }

    const existingToolbar = questionsPanel.querySelector('.stamped-questions-toolbar');
    const search =
      questionsPanel.querySelector(':scope > .stamped-questions-search-text, .stamped-questions-search-text');
    const filter =
      questionsPanel.querySelector(':scope > .stamped-questions-filter, .stamped-questions-filter');

    if (!search && !filter) {
      return;
    }

    const toolbar = existingToolbar || document.createElement('div');
    toolbar.className = 'stamped-questions-toolbar';

    if (search && search.parentElement !== toolbar) {
      toolbar.appendChild(search);
    }

    if (filter && filter.parentElement !== toolbar) {
      toolbar.appendChild(filter);
    }

    if (!existingToolbar) {
      const firstQuestion = questionsPanel.querySelector(
        '.stamped-question, .stamped-review:not(.stamped-questions .stamped-review), .stamped-pagination, #stamped-pagination-question',
      );
      questionsPanel.insertBefore(toolbar, firstQuestion || null);
    }
  };

  const showPanel = (panel) => {
    if (!panel) {
      return;
    }

    panel.hidden = false;
    panel.removeAttribute('aria-hidden');
    panel.style.setProperty('display', 'block', 'important');
    panel.style.removeProperty('visibility');
    panel.style.removeProperty('opacity');
    panel.style.removeProperty('height');
    panel.classList.add('stamped-split-section__panel--visible');
  };

  const showWidgetControls = (widget, selectors) => {
    selectors.forEach((selector) => {
      widget.querySelectorAll(selector).forEach(showPanel);
    });
  };

  const applySplitLayout = (wrapper) => {
    const widget = findWidget(wrapper);
    if (!widget) {
      return false;
    }

    const container = widget.querySelector('.stamped-container');
    if (!container) {
      return false;
    }

    if (container.querySelector('.stamped-loading') && !container.querySelector('.stamped-review, .stamped-questions, #stamped-questions-tab')) {
      return false;
    }

    const questionsTab = widget.querySelector('li#tab-questions');
    const reviewsTab = widget.querySelector('li#tab-reviews');

    if (questionsTab && questionsTab.dataset.stampedSplitPrimed !== 'true') {
      questionsTab.click();
      questionsTab.dataset.stampedSplitPrimed = 'true';

      window.setTimeout(() => {
        if (reviewsTab) {
          reviewsTab.click();
        }

        wrapper.dataset.stampedSplitReady = 'false';
        applySplitLayout(wrapper);
      }, 500);

      return false;
    }

    hideTabs(wrapper);

    const reviewsPanel = findReviewsPanel(widget);
    const questionsPanel = findQuestionsPanel(widget);
    const questionsTarget = wrapper.querySelector('[data-stamped-questions-target]');

    showWidgetControls(widget, REVIEW_UI_SELECTORS);
    showPanel(reviewsPanel);

    if (questionsPanel && questionsTarget && !questionsTarget.contains(questionsPanel)) {
      questionsTarget.appendChild(questionsPanel);
    }

    const questionsPanelRoot = relocateQuestionsControls(wrapper, questionsTarget) ||
      questionsPanel ||
      questionsTarget?.querySelector('#stamped-questions-tab, #stamped-questions, .stamped-questions');

    buildQuestionsToolbar(questionsPanelRoot);
    showWidgetControls(questionsPanelRoot || widget, QUESTION_UI_SELECTORS);
    showPanel(questionsPanel);

    hideReviewsSearch(wrapper);
    hideNativeSortSelects(wrapper);
    setupPaginationScroll(wrapper);

    wrapper.dataset.stampedSplitReady = 'true';
    return Boolean(reviewsPanel || questionsPanel);
  };

  const initWrapper = (wrapper) => {
    if (wrapper.dataset.stampedSplitReady === 'true') {
      return;
    }

    if (applySplitLayout(wrapper)) {
      return;
    }

    let attempts = 0;
    const maxAttempts = 120;
    const timer = window.setInterval(() => {
      attempts += 1;

      if (applySplitLayout(wrapper) || attempts >= maxAttempts) {
        window.clearInterval(timer);
      }
    }, 250);

    const observer = new MutationObserver(() => {
      if (applySplitLayout(wrapper)) {
        observer.disconnect();
        window.clearInterval(timer);
      }
    });

    observer.observe(wrapper, { childList: true, subtree: true });
  };

  const initAll = () => {
    ensureWrappers();
    document.querySelectorAll(WRAPPER_SELECTOR).forEach((wrapper) => {
      setupPaginationScroll(wrapper);
      initWrapper(wrapper);
    });
  };

  window.initStampedSplitSections = initAll;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  window.addEventListener('load', initAll);
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('stamped:init', () => {
    window.setTimeout(initAll, 100);
    window.setTimeout(initAll, 600);
    window.setTimeout(initAll, 1500);
  });
})();
